import React from 'react';
import { Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';

export type ListColumnWidth = 'content' | 'boundedContent' | 'fill' | 'protectedFill' | 'icon';

export const PROTECTED_FILL_COLUMN_TEMPLATE = 'minmax(8rem, 1fr)';

export type ListRowCell = {
  xs?: number;
  content: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noWrap?: boolean;
  fontWeight?: number;
  wrapTypography?: boolean;
  columnWidth?: ListColumnWidth;
};

export interface ListRowProps {
  cells: ListRowCell[];
  rightActions?: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  rowSx?: SxProps<Theme>;
  useParentColumns?: boolean;
}

const cellJustify = (align: ListRowCell['align']) => {
  if (align === 'left') return 'flex-start';
  if (align === 'right') return 'flex-end';
  return 'center';
};

const gridColumnForCell = (cell: Pick<ListRowCell, 'columnWidth' | 'xs'>): string => {
  if (cell.columnWidth === 'content') return 'max-content';
  if (cell.columnWidth === 'boundedContent') return 'fit-content(12rem)';
  if (cell.columnWidth === 'icon') return '2.5rem';
  if (cell.columnWidth === 'protectedFill') return PROTECTED_FILL_COLUMN_TEMPLATE;
  if (cell.columnWidth === 'fill') return 'minmax(0, 1fr)';
  return `minmax(0, ${cell.xs ?? 1}fr)`;
};

const isWrappingColumn = (columnWidth: ListColumnWidth | undefined) => columnWidth === 'fill' || columnWidth === 'protectedFill';

const ListRow: React.FC<ListRowProps> = ({ cells, rightActions, onMouseEnter, onMouseLeave, rowSx, useParentColumns = false }) => {
  const gridTemplateColumns = [
    ...cells.map(gridColumnForCell),
    ...(rightActions ? [`minmax(0, ${Math.max(1, 12 - cells.reduce((sum, c) => sum + (c.xs ?? 0), 0))}fr)`] : []),
  ].join(' ');
  const baseRowSx = {
    display: 'grid',
    gridColumn: useParentColumns ? '1 / -1' : undefined,
    gridTemplateColumns: useParentColumns ? 'subgrid' : gridTemplateColumns,
    columnGap: 1,
    alignItems: 'center',
  };

  return (
    <>
      <Grid
        sx={Array.isArray(rowSx) ? [baseRowSx, ...rowSx] : rowSx ? [baseRowSx, rowSx] : baseRowSx}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {cells.map((c, idx) => (
          <Grid
            key={idx}
            data-column-width={c.columnWidth}
            sx={{
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: cellJustify(c.align),
              overflow: isWrappingColumn(c.columnWidth) ? 'visible' : 'hidden',
            }}
          >
            {c.wrapTypography === false ? (
              c.content
            ) : (
              <Typography
                variant="body2"
                noWrap={c.noWrap ?? !isWrappingColumn(c.columnWidth)}
                sx={{
                  fontSize: 12,
                  width: '100%',
                  textAlign: c.align ?? 'center',
                  overflow: isWrappingColumn(c.columnWidth) ? 'visible' : 'hidden',
                  textOverflow: isWrappingColumn(c.columnWidth) ? 'clip' : 'ellipsis',
                  overflowWrap: isWrappingColumn(c.columnWidth) ? 'anywhere' : undefined,
                  wordBreak: isWrappingColumn(c.columnWidth) ? 'break-word' : undefined,
                  lineHeight: 1.35,
                  fontWeight: c.fontWeight,
                }}
              >
                {c.content}
              </Typography>
            )}
          </Grid>
        ))}
        {rightActions ? <Grid sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{rightActions}</Grid> : null}
      </Grid>
      <Grid sx={{ gridColumn: useParentColumns ? '1 / -1' : undefined }}>
        <Divider />
      </Grid>
    </>
  );
};

export default ListRow;
